/** Must match APPEARANCE_REQUIREMENTS on the backend — the values are stored verbatim. */
export type AppearanceRequirement = 'NOT_REQUIRED' | 'IN_PERSON' | 'BY_VIDEO';

export const APPEARANCE_LABELS: Record<AppearanceRequirement, string> = {
  NOT_REQUIRED: 'Client does not need to appear',
  IN_PERSON: 'Client must appear in person',
  BY_VIDEO: 'Client must appear, by video',
};

interface CourtEventDto {
  CourtEvent_guid: string;
  CourtEvent_Start: number;
  CourtEvent_End: number;
  CourtEvent_Title: string;
  CourtEvent_Desc: string;
  // Present only on manager-authenticated responses. The client portal never receives these.
  CourtEvent_AppearanceRequirement?: AppearanceRequirement;
  /** Null means nobody has answered the appearance question — the value is just the default. */
  CourtEvent_AppearanceSetBy?: string | null;
  CourtEvent_AppearanceSetAt?: string | null;
  /** False for reminders and deadlines, where the appearance question does not apply. */
  CourtEvent_IsHearing?: boolean;
  Ticket_guid: string;
}

interface TicketDto {
  Ticket_guid: string;
  Ticket_Number: string;
  Ticket_Representation: string;
  Ticket_Address: string;
  Ticket_Citation: string;
  Ticket_City: string;
  Ticket_County: string;
  Ticket_LicenseType: string;
  Ticket_Zone: string;
  Ticket_Url: string;
  Ticket_CourtDate: string;
  Ticket_CreatedAt: string;
  Ticket_Active: boolean;
  Court_id: number | null;
}

interface UserDto {
  User_guid: string;
  User_FirstName: string;
  User_LastName: string;
  User_Active: boolean;
  User_DOB: string | null;
}

interface FullCourtEventDto {
  courtEvent: CourtEventDto;
  ticket: TicketDto;
  user: UserDto;
}

interface CreateCourtEventDto {
  CourtEvent_Start: number;
  CourtEvent_End: number;
  CourtEvent_Title?: string;
  CourtEvent_Desc?: string;
  Ticket_guid: string; // Use guid instead of id since that's what frontend has
  calendarEventTitle?: string;
  calendarEventLocation?: string;
  calendarEventBody?: string;
}

interface UpdateCourtEventDto {
  CourtEvent_guid: string;
  CourtEvent_Start?: number;
  CourtEvent_End?: number;
  CourtEvent_Title?: string;
  CourtEvent_Desc?: string;
  CourtEvent_AppearanceRequirement?: AppearanceRequirement;
  Ticket_guid?: string;
  calendarEventTitle?: string;
  calendarEventLocation?: string;
  calendarEventBody?: string;
}

class CourtEventApi {
  static async getCourtEvent(CourtEvent_guid: string): Promise<FullCourtEventDto> {
    const queryParams = new URLSearchParams();
    queryParams.append('CourtEvent_guid', CourtEvent_guid);

    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/court-event/?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to fetch court event' }));
      throw new Error(errorData.error || 'Failed to fetch court event');
    }

    return await response.json();
  }

  static async listCourtEvents(params: {
    User_guid?: string;
    Ticket_guid?: string;
    Court_guid?: string;
    CourtEvent_Start_From?: number;
    CourtEvent_Start_To?: number;
  }): Promise<CourtEventDto[]> {
    console.log('Listing court events with params:', params);
    const queryParams = new URLSearchParams();
    
    if (params.User_guid) queryParams.append('User_guid', params.User_guid);
    if (params.Ticket_guid) queryParams.append('Ticket_guid', params.Ticket_guid);
    if (params.Court_guid) queryParams.append('Court_guid', params.Court_guid);
    if (params.CourtEvent_Start_From) queryParams.append('CourtEvent_Start_From', params.CourtEvent_Start_From.toString());
    if (params.CourtEvent_Start_To) queryParams.append('CourtEvent_Start_To', params.CourtEvent_Start_To.toString());

    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/court-event/list?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to fetch court events' }));
      throw new Error(errorData.error || 'Failed to fetch court events');
    }

    return await response.json();
  }

  static async createCourtEvent(courtEvent: CreateCourtEventDto): Promise<CourtEventDto> {
    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/court-event/`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(courtEvent),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to create court event' }));
      throw new Error(errorData.error || 'Failed to create court event');
    }

    return await response.json();
  }

  /**
   * The PATCH response carries this when the appearance answer changed, so the screen can report
   * what actually reached the client instead of assuming a send happened.
   */
  static async updateCourtEvent(
    courtEvent: UpdateCourtEventDto,
  ): Promise<
    CourtEventDto & {
      appearanceNotification?: { sent: boolean; skippedReason: string | null } | null;
    }
  > {
    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/court-event/`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(courtEvent),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to update court event' }));
      throw new Error(errorData.error || 'Failed to update court event');
    }

    return await response.json();
  }

  static async deleteCourtEvent(CourtEvent_guid: string): Promise<{ message: string; courtEvent: CourtEventDto }> {
    const queryParams = new URLSearchParams();
    queryParams.append('CourtEvent_guid', CourtEvent_guid);

    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/court-event/?${queryParams.toString()}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Failed to delete court event' }));
      throw new Error(errorData.error || 'Failed to delete court event');
    }

    return await response.json();
  }
}

export default CourtEventApi;
export type { CourtEventDto, FullCourtEventDto, CreateCourtEventDto, UpdateCourtEventDto, TicketDto, UserDto };
